import type { DeeplinkPreset } from '../interfaces/deeplinkCatalog'

export const LOCAL_STORAGE_CUSTOM_DEEPLINKS_KEY = 'habit_custom_deeplinks'

/**
 * Retrieves user custom deeplinks from localStorage safely.
 */
export function getLocalCustomDeeplinks(): DeeplinkPreset[] {
  if (typeof localStorage === 'undefined') {
    return []
  }

  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_CUSTOM_DEEPLINKS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
  } catch {
    return []
  }
}

/**
 * Persists user custom deeplinks to localStorage safely.
 */
export function saveLocalCustomDeeplinks(items: DeeplinkPreset[]): void {
  if (typeof localStorage === 'undefined') {
    return
  }

  try {
    localStorage.setItem(
      LOCAL_STORAGE_CUSTOM_DEEPLINKS_KEY,
      JSON.stringify(items),
    )
  } catch (err) {
    console.error('Failed to save custom deeplinks to localStorage', err)
  }
}

/**
 * Merges local and remote custom presets by id.
 * Remote presets override local ones with the same ID if remote is newer or present.
 */
export function mergeCustomDeeplinks(
  localItems: DeeplinkPreset[],
  remoteItems: DeeplinkPreset[],
): DeeplinkPreset[] {
  const map = new Map<string, DeeplinkPreset>()

  for (const item of localItems) {
    map.set(item.id, item)
  }

  for (const item of remoteItems) {
    const existing = map.get(item.id)
    if (!existing) {
      map.set(item.id, item)
    } else {
      const existingTime = existing.updated_at
        ? new Date(existing.updated_at).getTime()
        : 0
      const remoteTime = item.updated_at
        ? new Date(item.updated_at).getTime()
        : 0
      if (remoteTime >= existingTime) {
        map.set(item.id, item)
      }
    }
  }

  return Array.from(map.values())
}

/**
 * Fetches user custom deeplinks from Supabase.
 */
export async function fetchUserCustomDeeplinks(
  // biome-ignore lint/suspicious/noExplicitAny: Supabase schema dynamic typing
  supabaseClient: any,
  userId: string,
): Promise<DeeplinkPreset[]> {
  if (!supabaseClient || !userId) return []

  try {
    const { data, error } = await supabaseClient
      .schema('habit')
      .from('custom_deeplinks')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (error) {
      console.warn('Failed to fetch user custom deeplinks from cloud:', error)
      return []
    }

    return (data ?? []) as DeeplinkPreset[]
  } catch (err) {
    console.warn('Exception fetching user custom deeplinks:', err)
    return []
  }
}

/**
 * Fetches public community deeplinks from Supabase.
 */
export async function fetchCommunityDeeplinks(
  // biome-ignore lint/suspicious/noExplicitAny: Supabase schema dynamic typing
  supabaseClient: any,
): Promise<DeeplinkPreset[]> {
  if (!supabaseClient) return []

  try {
    const { data, error } = await supabaseClient
      .schema('habit')
      .from('custom_deeplinks')
      .select('*')
      .eq('is_public', true)
      .order('created_at', { ascending: false })

    if (error) {
      console.warn('Failed to fetch community deeplinks:', error)
      return []
    }

    return (data ?? []) as DeeplinkPreset[]
  } catch (err) {
    console.warn('Exception fetching community deeplinks:', err)
    return []
  }
}

/**
 * Inserts a new custom deeplink into localStorage and Supabase (if authenticated).
 */
export async function insertCustomDeeplink(
  // biome-ignore lint/suspicious/noExplicitAny: Supabase schema dynamic typing
  supabaseClient: any,
  userId: string | null | undefined,
  item: Omit<DeeplinkPreset, 'id' | 'created_at' | 'updated_at'>,
): Promise<DeeplinkPreset> {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

  const now = new Date().toISOString()
  const newPreset: DeeplinkPreset = {
    id,
    user_id: userId ?? null,
    name: item.name.trim(),
    scheme_url: item.scheme_url.trim(),
    category: item.category || 'General',
    icon: item.icon || '📱',
    is_public: item.is_public ?? false,
    created_at: now,
    updated_at: now,
  }

  // Update local storage first
  const current = getLocalCustomDeeplinks()
  saveLocalCustomDeeplinks([newPreset, ...current])

  // Sync to Supabase if authenticated
  if (supabaseClient && userId) {
    try {
      const { data, error } = await supabaseClient
        .schema('habit')
        .from('custom_deeplinks')
        .insert({
          id: newPreset.id,
          user_id: userId,
          name: newPreset.name,
          scheme_url: newPreset.scheme_url,
          category: newPreset.category,
          icon: newPreset.icon,
          is_public: newPreset.is_public,
        })
        .select()
        .single()

      if (!error && data) {
        return data as DeeplinkPreset
      }
    } catch (err) {
      console.warn('Failed to sync inserted custom deeplink to cloud:', err)
    }
  }

  return newPreset
}

/**
 * Updates an existing custom deeplink in localStorage and Supabase.
 */
export async function updateCustomDeeplink(
  // biome-ignore lint/suspicious/noExplicitAny: Supabase schema dynamic typing
  supabaseClient: any,
  userId: string | null | undefined,
  item: DeeplinkPreset,
): Promise<DeeplinkPreset> {
  const now = new Date().toISOString()
  const updatedPreset: DeeplinkPreset = {
    ...item,
    name: item.name.trim(),
    scheme_url: item.scheme_url.trim(),
    updated_at: now,
  }

  // Update localStorage
  const current = getLocalCustomDeeplinks()
  const nextList = current.map((p) =>
    p.id === updatedPreset.id ? updatedPreset : p,
  )
  saveLocalCustomDeeplinks(nextList)

  // Sync to Supabase
  if (supabaseClient && userId) {
    try {
      const { data, error } = await supabaseClient
        .schema('habit')
        .from('custom_deeplinks')
        .update({
          name: updatedPreset.name,
          scheme_url: updatedPreset.scheme_url,
          category: updatedPreset.category,
          icon: updatedPreset.icon,
          is_public: updatedPreset.is_public,
          updated_at: now,
        })
        .eq('id', updatedPreset.id)
        .eq('user_id', userId)
        .select()
        .single()

      if (!error && data) {
        return data as DeeplinkPreset
      }
    } catch (err) {
      console.warn('Failed to sync updated custom deeplink to cloud:', err)
    }
  }

  return updatedPreset
}

/**
 * Deletes a custom deeplink from localStorage and Supabase.
 */
export async function deleteCustomDeeplink(
  // biome-ignore lint/suspicious/noExplicitAny: Supabase schema dynamic typing
  supabaseClient: any,
  userId: string | null | undefined,
  id: string,
): Promise<boolean> {
  // Delete from localStorage
  const current = getLocalCustomDeeplinks()
  saveLocalCustomDeeplinks(current.filter((p) => p.id !== id))

  // Delete from Supabase
  if (supabaseClient && userId) {
    try {
      await supabaseClient
        .schema('habit')
        .from('custom_deeplinks')
        .delete()
        .eq('id', id)
        .eq('user_id', userId)
    } catch (err) {
      console.warn('Failed to delete custom deeplink from cloud:', err)
    }
  }

  return true
}
